import React from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { useT, useLocale } from '../../i18n/LocaleContext';
import { useData } from '../../data/DataContext';
import { formatCurrency, formatDate, payerLabel } from '../../utils/format';

const STATUS_TONE = { paid: 'paid', pending: 'pending', cancelled: 'inactive', refunded: 'completed' };
const TYPE_TONE = { cotisation: 'cotisation', pelerinage: 'pelerinage', offrande: 'offrande' };

export default function PaymentDetailModal({ isOpen, onClose, payment, onEdit, onMarkPaid }) {
  const t = useT();
  const { locale } = useLocale();
  const { memberById, eventById } = useData();

  if (!payment) return null;
  const event = payment.eventId ? eventById(payment.eventId) : null;
  const payer = payerLabel(payment, memberById);
  const isGuest = !payment.memberId && Boolean(payment.payerName);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('payments.view_payment')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {payment.status === 'pending' && onMarkPaid && (
            <Button variant="primary" onClick={onMarkPaid}>{t('payments.mark_as_paid')}</Button>
          )}
          {onEdit && <Button variant="gold" onClick={onEdit}>{t('common.edit')}</Button>}
        </>
      )}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
        <div>
          <div className="mono" style={{ fontSize: '13px' }}>{payment.code || payment.id}</div>
          <div style={{ fontFamily: "'Cinzel', serif", fontSize: '22px', color: 'var(--blue-deep)', marginTop: '4px' }}>
            {formatCurrency(payment.amount, locale)}
          </div>
        </div>
        <Badge tone={STATUS_TONE[payment.status]}>{t(`payments.statuses.${payment.status}`)}</Badge>
      </div>

      <div className="detail-grid">
        <div>
          <div className="detail-item-label">{t('payments.field_payer')}</div>
          <div className="detail-item-value" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {payer}
            {isGuest && <Badge tone="pending">{t('payments.payer_guest_label')}</Badge>}
          </div>
        </div>
        <div>
          <div className="detail-item-label">{t('payments.field_type')}</div>
          <div className="detail-item-value">
            <Badge tone={TYPE_TONE[payment.type]}>{t(`payments.types.${payment.type}`)}</Badge>
          </div>
        </div>
        <div>
          <div className="detail-item-label">{t('payments.field_date')}</div>
          <div className="detail-item-value">{formatDate(payment.date, t)}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('payments.field_method')}</div>
          <div className="detail-item-value">{t(`payments.methods.${payment.method}`)}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('payments.field_event')}</div>
          <div className="detail-item-value">{event ? event.name : '—'}</div>
        </div>
        {payment.externalRef && (
          <div>
            <div className="detail-item-label">{t('payments.field_external_ref')}</div>
            <div className="detail-item-value mono-value">{payment.externalRef}</div>
          </div>
        )}
        <div>
          <div className="detail-item-label">{t('payments.field_notes')}</div>
          <div className="detail-item-value">{payment.notes || '—'}</div>
        </div>
      </div>
    </Modal>
  );
}
